import React, { FC, memo } from 'react';
import { Document, Page, StyleSheet, Text, View } from '@react-pdf/renderer';

import styles, { BASE_FONT_SIZE, PDF_PRIMARY_COLOR } from '../pdfStyles';
import PdfLogoComponent from '../PdfComponents/PdfLogo';
import PdfTableComponent, { PdfTablePropsColumn, PdfTablePropsRow } from '../PdfComponents/PdfTable';
import { getDateByFormat } from '../../../utils/dateUtils';
import { voucherStatusesEnum } from '../../../api/vouchers/helpers';
import { VouchersPdfPropsType } from './voucherHelpers';

/**
 * Hebrew (RTL) receipt — used ONLY by the customer-facing tour-registration
 * render path (renderReceiptPdfBuffer({ language: 'he' })). The back-office
 * `ReceiptPdf` (English/LTR) is intentionally left untouched. Same pdfInfo data,
 * mirrored layout + translated labels.
 */

// Label dictionary. Keys mirror the English strings the receipt builder emits.
const HE: Record<string, string> = {
    companyName: 'פגסוס תיירות ונסיעות בע״מ',
    to: 'לכבוד',
    for: 'עבור',
    iata: "מס' IATA",
    vat: 'ח.פ. / עוסק',
    issueDate: 'תאריך הנפקה',
    travelFile: 'מספר תיק',
    original: 'מקור',
    deposited: 'שולם',
    cancelled: 'מבוטל',
    cancelledBy: 'בוטל',
    cancelledByUser: 'ע״י',
    cancelledOn: 'בתאריך',
    receiptNo: "קבלה מס'",
    phone: 'טלפון',
    issuedBy: 'הופק על ידי',
    issuedUsing: 'הופק באמצעות Taytan',
    page: 'עמוד',
    of: 'מתוך',
    creditCard: 'כרטיס אשראי',
    total: 'סה"כ',
    // table column titles (by fieldName)
    paymentMethod: 'אמצעי תשלום',
    description: 'תיאור',
    dueDate: 'תאריך ערך',
    firstPayment: 'תשלום ראשון',
    additionalPayments: 'תשלומים נוספים',
    creditPayments: 'מס׳ תשלומי קרדיט',
    amount: 'סכום',
    bank: 'בנק',
    receivingBank: 'בנק יעד',
    account: 'חשבון',
    number: "צ'ק מס'",
    // body-box labels (by English label)
    'Credit Company': 'חברת אשראי',
    'Card No.': 'מספר כרטיס',
    'Exp. Date': 'תוקף',
    Reference: 'אסמכתא',
    'Supplier Id': 'מזהה ספק',
    Terminal: 'מסוף'
};

const rtl = StyleSheet.create({
    page: { fontSize: BASE_FONT_SIZE, padding: '100 0 30', fontFamily: 'PdfFont', direction: 'rtl' },
    rowReverse: { flexDirection: 'row-reverse', justifyContent: 'space-between' },
    right: { textAlign: 'right' },
    headerDetailLine: { flexDirection: 'row-reverse', paddingVertical: 1 },
    badge: {
        backgroundColor: PDF_PRIMARY_COLOR,
        color: '#fff',
        fontSize: BASE_FONT_SIZE,
        padding: '4 10',
        borderRadius: 2,
        textAlign: 'center'
    },
    receiptTitleBox: {
        borderColor: PDF_PRIMARY_COLOR,
        borderWidth: 1,
        borderRadius: 3,
        padding: '6 10',
        marginVertical: 6
    }
});

// Translate the payment-method value shown in the F.O.P cell.
const translatePaymentMethod = (value: any): any => {
    if (value === 'Credit Card') return HE.creditCard;
    if (typeof value === 'string' && value.startsWith('TOTAL')) {
        // "TOTAL (USD)" → 'סה"כ (USD)'
        const currency = value.replace(/^TOTAL\s*/, '');
        return `${HE.total} ${currency}`.trim();
    }
    return value;
};

// Reverse + translate the columns so they read right-to-left with Hebrew heads.
// Centred columns (alignReceiptColumns) stay centred; text columns align right.
const toHebrewColumns = (columns: PdfTablePropsColumn[] = []): PdfTablePropsColumn[] =>
    [...columns].reverse().map((c) => ({
        ...c,
        title: c.title ? HE[c.fieldName] || c.title : c.title,
        style: { ...(c.style || {}), textAlign: c.style?.textAlign === 'center' ? 'center' : 'right' }
    }));

const toHebrewRow = (row: PdfTablePropsRow = {}): PdfTablePropsRow => ({
    ...row,
    paymentMethod: translatePaymentMethod((row as any).paymentMethod)
});

// Fixed label-column width keeps every label right-aligned to the same edge and
// every value's right edge aligned → a clean, indented two-column form.
const LABEL_COL_W = 84;

const NBSP = String.fromCharCode(160);
// Keep a phone number on one line + render it left-to-right.
const formatPhone = (raw: any): string =>
    String(raw ?? '')
        .trim()
        .replace(/\s+/g, NBSP);

/**
 * One "value : label" line. The label sits in a fixed-width, right-aligned cell
 * (so all labels/values line up); inside that cell the colon hugs the word's
 * left edge (":מס' IATA"), so the ":" reads as part of the word rather than
 * floating next to the value.
 */
const LabelValueRow: FC<{
    label: string;
    value?: string;
    bold?: boolean;
    labelColor?: string;
    valueLtr?: boolean;
}> = ({ label, value, bold, labelColor = '#777', valueLtr }) => (
    <View style={{ flexDirection: 'row', justifyContent: 'flex-end', paddingVertical: 1 }}>
        <Text
            style={{
                flex: 1,
                fontSize: BASE_FONT_SIZE,
                fontWeight: bold ? 'bold' : 'normal',
                textAlign: 'right',
                // Phone/number values must read left-to-right even in the RTL
                // receipt (otherwise bidi reorders the digits).
                ...(valueLtr ? { direction: 'ltr' as const } : {})
            }}
        >
            {value || ''}
        </Text>
        <View style={{ width: LABEL_COL_W, flexDirection: 'row', justifyContent: 'flex-end', marginLeft: 6 }}>
            <Text style={{ fontSize: BASE_FONT_SIZE, color: labelColor }}>:</Text>
            <Text style={{ fontSize: BASE_FONT_SIZE, color: labelColor }}>{label}</Text>
        </View>
    </View>
);

/**
 * A right-aligned line built from separate pieces laid out right-to-left. react-pdf takes a
 * line's direction from its first letter and reorders mixed Hebrew / English / digits on its
 * own, so "בוטל ע״י Moish Shamir בתאריך 08/10/2026" came out scrambled; one Text per piece
 * keeps each in place.
 */
const RtlLine: FC<{ parts: (string | false | null | undefined)[]; color: string; marginTop: number }> = ({
    parts,
    color,
    marginTop
}) => (
    <View style={{ flexDirection: 'row-reverse', flexWrap: 'wrap', marginTop, marginHorizontal: 10 }}>
        {(parts.filter(Boolean) as string[]).map((part, i, all) => (
            <Text key={i} style={{ fontSize: BASE_FONT_SIZE - 1, color, marginLeft: all[i + 1] === ':' ? 0 : 4 }}>
                {part}
            </Text>
        ))}
    </View>
);

/** "label:" with the colon placed on the word's left by layout, not left to react-pdf's bidi. */
const RtlLabel: FC<{ label: string; style: any }> = ({ label, style }) => (
    <View style={{ flexDirection: 'row-reverse' }}>
        <Text style={style}>{label}</Text>
        <Text style={style}>:</Text>
    </View>
);

const ReceiptPdfHe: FC<VouchersPdfPropsType> = (props: VouchersPdfPropsType) => {
    const {
        info: {
            travelFileNumber,
            remark,
            paymentMethodType,
            paymentMethodObject,
            isDeposited,
            pdfInfo: {
                companyInfo,
                userName,
                passenger,
                voucherCertification = 'Original',
                pdfImageSource,
                pdfBodyBoxData,
                bottomDetails,
                columns,
                rows,
                summaryLine,
                receivingBankText,
                cancelledByName,
                cancelledAtText
            }
        },
        number: voucherNumber,
        status,
        createdAt
    } = props as any;
    // Same rule as the English receipt: CANCELLED replaces the paid banner.
    const isCancelled = status === voucherStatusesEnum.CANCEL;

    const heColumns = toHebrewColumns(columns);
    // A one-line text cell is drawn as a bare Text that does not take the column's right alignment
    // (multi-line cells set it per line), so "כרטיס אשראי" sat at the left of its cell. The table's
    // array form carries a per-cell style — use it for one-line text in right-aligned columns.
    const rightAligned = new Set(heColumns.filter((c) => c.style?.textAlign === 'right').map((c) => c.fieldName));
    const heRows = ((rows || []) as PdfTablePropsRow[]).map(toHebrewRow).map((row) => {
        const out: PdfTablePropsRow = { ...row };
        for (const field of rightAligned) {
            const v = out[field];
            if (typeof v === 'string' && v && !/\r?\n/.test(v)) out[field] = [{ text: v, style: { textAlign: 'right' } }];
        }
        return out;
    });
    const heSummary = summaryLine ? toHebrewRow(summaryLine) : undefined;

    const passengerName = passenger?.name || passenger?.firstName || '';
    const bottomText = Array.isArray(bottomDetails)
        ? bottomDetails
              .map((b: any) => b?.name)
              .filter(Boolean)
              .join(', ')
        : bottomDetails;

    const headerLines: Array<{ label: string; value: string }> = [];
    // IATA number intentionally hidden on the Hebrew customer receipt.
    if (companyInfo?.vat) {
        // Match PdfHeaderDetails' dash formatting (e.g. "511636292" → "51-1636292").
        const formattedVat = String(companyInfo.vat).replace(/^(\d{2})(\d+)$/, '$1-$2');
        headerLines.push({ label: HE.vat, value: formattedVat });
    }
    headerLines.push({
        label: HE.issueDate,
        value: getDateByFormat(props.issueDate || createdAt, 'dd-MMM-yy', '')
    });
    if (travelFileNumber) headerLines.push({ label: HE.travelFile, value: String(travelFileNumber) });

    return (
        <Document title={`${HE.receiptNo} ${voucherNumber || ''}`}>
            <Page wrap size="A4" style={rtl.page}>
                <PdfLogoComponent pdfImageSource={pdfImageSource} />

                <View style={styles.padding0_10}>
                    {/* Recipient (right) + company header details (left) */}
                    <View style={[styles.section, rtl.rowReverse]}>
                        <View style={{ width: 270, alignItems: 'flex-end' }}>
                            <RtlLabel
                                label={HE.to}
                                style={{ fontSize: BASE_FONT_SIZE - 1, color: '#888', marginBottom: 2 }}
                            />
                            {passengerName ? (
                                <Text style={{ fontWeight: 'bold', fontSize: BASE_FONT_SIZE + 1 }}>
                                    {passengerName}
                                </Text>
                            ) : null}
                            {/* Address, city + postal code, country — as on the English receipt */}
                            {[
                                passenger?.address,
                                [passenger?.city, passenger?.postalCode].filter(Boolean).join(' '),
                                typeof passenger?.country === 'string' ? passenger.country : ''
                            ]
                                .map((line) => String(line ?? '').trim())
                                .filter(Boolean)
                                .map((line, i) => (
                                    <Text key={i} style={{ fontSize: BASE_FONT_SIZE, color: '#555', textAlign: 'right' }}>
                                        {line}
                                    </Text>
                                ))}
                            {passenger?.phone?.number || passenger?.phone ? (
                                // Custom row (not LabelValueRow) so the long LTR
                                // phone keeps a clear gap from the label.
                                <View
                                    style={{
                                        flexDirection: 'row-reverse',
                                        alignItems: 'baseline',
                                        marginTop: 1
                                    }}
                                >
                                    <RtlLabel label={HE.phone} style={{ fontSize: BASE_FONT_SIZE, color: '#777' }} />
                                    <Text style={{ fontSize: BASE_FONT_SIZE, marginRight: 8 }}>
                                        {formatPhone(passenger?.phone?.number || passenger?.phone)}
                                    </Text>
                                </View>
                            ) : null}
                        </View>

                        <View style={{ width: 210 }}>
                            <Text
                                style={{
                                    fontSize: BASE_FONT_SIZE + 1,
                                    fontWeight: 'bold',
                                    color: PDF_PRIMARY_COLOR,
                                    textAlign: 'right',
                                    marginBottom: 3
                                }}
                            >
                                {(companyInfo as any)?.nameHe || HE.companyName}
                            </Text>
                            {headerLines.map((item, idx) => (
                                <LabelValueRow key={idx} label={item.label} value={item.value} bold />
                            ))}
                        </View>
                    </View>

                    {/* Certification badge (right-aligned) */}
                    <View style={[styles.margin2_10, { alignItems: 'flex-start' }]}>
                        <Text style={rtl.badge}>{HE[voucherCertification] || HE.original}</Text>
                    </View>

                    {/* CANCELLED, else DEPOSITED */}
                    {isCancelled || isDeposited ? (
                        <View style={[styles.section, styles.alignCenter]} wrap={false}>
                            <Text style={[styles.paymentStatus, styles.marginTopMinus24BottomMinus14]}>
                                {isCancelled ? HE.cancelled : HE.deposited}
                            </Text>
                        </View>
                    ) : null}

                    {/* Receipt number + "For" */}
                    <View style={[styles.section, styles.alignCenter]} wrap={false}>
                        <View style={rtl.receiptTitleBox}>
                            <Text style={{ fontWeight: 'bold', textAlign: 'center' }}>
                                {HE.receiptNo} {voucherNumber || ''}
                            </Text>
                        </View>
                        {bottomText && bottomText !== 'null' && bottomText !== 'undefined' ? (
                            <Text style={{ textAlign: 'center' }}>
                                {HE.for}: {bottomText}
                            </Text>
                        ) : null}
                    </View>

                    {/* Body box (credit-card details), RTL */}
                    {pdfBodyBoxData?.length ? (
                        <View style={[styles.section, styles.padding4_0]}>
                            {pdfBodyBoxData.map((line: any, idx: number) => (
                                <LabelValueRow
                                    key={idx}
                                    label={HE[line.label] || line.label}
                                    value={line.value || ' '}
                                    labelColor="#555"
                                />
                            ))}
                        </View>
                    ) : null}

                    {/* F.O.P table — reversed columns + Hebrew heads */}
                    <View style={[styles.section, styles.marginBottom10px]}>
                        <PdfTableComponent
                            columns={heColumns}
                            rows={heRows}
                            summaryLine={heSummary}
                            summaryLineStyle={styles.fontBoldAndBig1}
                            headerLineStyle={styles.fontBoldAndBig2}
                        />
                    </View>

                    {/* Receiving bank — the account the money went into (TB's בנק יעד) */}
                    {receivingBankText &&
                    (paymentMethodType === 'Credit Card' || paymentMethodType === 'Bank Transfer') ? (
                        <View style={[styles.section, styles.marginBottom10px]}>
                            <LabelValueRow label={HE.receivingBank} value={receivingBankText} />
                        </View>
                    ) : null}

                    {/* Remark */}
                    {remark ? (
                        <View style={[styles.section, styles.marginBottom10px]}>
                            <Text style={[styles.littleText, rtl.right]}>{remark}</Text>
                        </View>
                    ) : null}

                    {/* No creator (a receipt written by the payment flow) — no empty "issued by" line,
                        as on the English receipt */}
                    {String(userName || '').trim() ? (
                        <RtlLine parts={[HE.issuedBy, ':', userName]} color="#6b7280" marginTop={6} />
                    ) : null}
                    {isCancelled && (cancelledByName || cancelledAtText) ? (
                        <RtlLine
                            parts={[
                                cancelledByName ? `${HE.cancelledBy} ${HE.cancelledByUser}` : HE.cancelledBy,
                                cancelledByName,
                                cancelledAtText && HE.cancelledOn,
                                cancelledAtText
                            ]}
                            color="#b42318"
                            marginTop={2}
                        />
                    ) : null}
                </View>

                {/* Footer (Hebrew) */}
                <View style={{ position: 'absolute', bottom: 10, left: 0, right: 0, width: '100%' }} fixed>
                    <View style={{ margin: '0 20 2 0', alignItems: 'flex-start' }}>
                        <Text style={{ fontSize: BASE_FONT_SIZE - 4 }}>{HE.issuedUsing}</Text>
                    </View>
                    <View style={[styles.borderTop, rtl.rowReverse, { padding: '0 20' }]}>
                        <Text style={[styles.littleText]}>
                            {`${companyInfo?.address || ''} ${HE.phone}: ${companyInfo?.phone || ''} ${
                                companyInfo?.website || ''
                            }`}
                        </Text>
                        <Text
                            style={[styles.littleText]}
                            render={({ pageNumber, totalPages }) =>
                                `${HE.page} ${pageNumber} ${HE.of} ${totalPages}`
                            }
                        />
                    </View>
                </View>
            </Page>
        </Document>
    );
};

export default memo(ReceiptPdfHe);
